// Custom Hook for Grill The Grid Episodes Data
import { useState, useEffect, useCallback, useMemo } from 'react';
import { loadCSV } from '../utils/csvLoader';

/**
 * Custom hook for loading Grill The Grid episodes data
 * @returns {Object} - Episodes data, loading state, error state, and utility functions
 */
export const useGrillTheGridEpisodes = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await loadCSV('/datasets/F1Data/grill_the_grid_episodes.csv');
      setData(result);
    } catch (err) {
      setError(err.message || 'Failed to load Grill The Grid episodes data');
      console.error('Grill The Grid episodes loading error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const refetch = useCallback(() => {
    loadData();
  }, [loadData]);

  // Get data grouped by year
  const dataByYear = useMemo(() => {
    if (!data) return {};
    
    const grouped = {};
    data.forEach(row => {
      const year = row.year;
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(row);
    });
    
    return grouped;
  }, [data]);

  // Get data for a specific year
  const getDataByYear = useCallback((year) => {
    return dataByYear[year] || [];
  }, [dataByYear]);

  // Get available years
  const availableYears = useMemo(() => {
    if (!data) return [];
    const years = [...new Set(data.map(row => row.year))];
    return years.sort((a, b) => b - a);
  }, [data]);

  // Get episodes for a specific year (sorted by round)
  const getEpisodesByYear = useCallback((year) => {
    const yearData = getDataByYear(year);
    return yearData.sort((a, b) => a.round - b.round);
  }, [getDataByYear]);

  return {
    data,
    dataByYear,
    loading,
    error,
    refetch,
    getDataByYear,
    availableYears,
    getEpisodesByYear
  };
};
